import React, { useState, useRef } from "react"
import { useTranslation } from "react-i18next"
import * as XLSX from "xlsx"
import {
  Box,
  Stack,
  Typography,
  Avatar,
  Button,
  LinearProgress,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material"
import UploadFileIcon from "@mui/icons-material/UploadFile"
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import { getProducts, deleteProduct, postProduct } from "../../helpers/api_helper"
import {
  generateDescription,
  buildCategoryArray,
  UNIT_MAP,
} from "../../helpers/catalogDecoder"

const BATCH_SIZE = 50
const SHEET_INDEX = 6 // Sheet 7 (0-indexed)

const CatalogUpdateTab = ({ onSnackbar }) => {
  const { t } = useTranslation()
  const fileInputRef = useRef(null)

  const [excelRows, setExcelRows] = useState([])
  const [fileName, setFileName] = useState("")
  const [loading, setLoading] = useState(false)
  const [running, setRunning] = useState(false)
  const [phase, setPhase] = useState("") // "deleting" | "importing"
  const [progress, setProgress] = useState({ done: 0, total: 0, errors: 0 })
  const [result, setResult] = useState(null) // { deleted, imported, errors }
  const [log, setLog] = useState([])

  // ── Step 1: Parse Excel ─────────────────────────────────────
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    setLoading(true)
    setFileName(file.name)
    setResult(null)
    setLog([])

    try {
      const data = await file.arrayBuffer()
      const workbook = XLSX.read(data, { type: "array" })

      const sheetName = workbook.SheetNames[SHEET_INDEX]
      if (!sheetName) {
        throw new Error(`Sheet ${SHEET_INDEX + 1} not found. Has ${workbook.SheetNames.length} sheets.`)
      }

      const worksheet = workbook.Sheets[sheetName]
      const rows = XLSX.utils.sheet_to_json(worksheet, { defval: "" })

      if (rows.length === 0) throw new Error("Sheet 7 is empty")

      setExcelRows(rows)
      onSnackbar?.({
        open: true,
        message: `${t("Parsed")} ${rows.length} ${t("rows from")} "${sheetName}"`,
        severity: "success",
      })
    } catch (err) {
      console.error("Excel parse error:", err)
      onSnackbar?.({ open: true, message: err.message, severity: "error" })
      setExcelRows([])
    } finally {
      setLoading(false)
      if (fileInputRef.current) fileInputRef.current.value = ""
    }
  }

  // ── Step 2: Delete all existing + reimport from Excel ────────
  const handleDeleteAndReimport = async () => {
    if (excelRows.length === 0) return
    if (!window.confirm(
      t("This will DELETE all existing products and reimport from Excel.") +
      "\n\n" + excelRows.length + " " + t("products will be imported.") +
      "\n\n" + t("Are you sure?")
    )) return

    setRunning(true)
    setResult(null)
    const logEntries = []
    const addLog = (msg, ok = true) => {
      logEntries.push({ msg, ok, time: new Date().toLocaleTimeString() })
      setLog([...logEntries])
    }

    try {
      // ── Phase 1: Fetch & delete all existing products ──────
      setPhase("deleting")
      addLog(t("Fetching all existing products..."))
      const existing = await getProducts()
      addLog(`${t("Found")} ${existing.length} ${t("products in DB")}`)

      let deleted = 0
      if (existing.length > 0) {
        // Delete in batches
        setProgress({ done: 0, total: existing.length, errors: 0 })

        for (let i = 0; i < existing.length; i += BATCH_SIZE) {
          const batch = existing.slice(i, i + BATCH_SIZE)
          const ids = batch.map(p => ({ id: p.id }))
          try {
            await deleteProduct({ resource: ids })
            deleted += batch.length
          } catch (err) {
            addLog(`${t("Delete batch error")}: ${err.message}`, false)
            deleted += batch.length // count anyway, some may have been deleted
          }
          setProgress({ done: deleted, total: existing.length, errors: 0 })
        }
        addLog(`${t("Deleted")} ${deleted} ${t("products")}`)
      }

      // ── Phase 2: Import fresh from Excel ───────────────────
      setPhase("importing")
      setProgress({ done: 0, total: excelRows.length, errors: 0 })
      let imported = 0
      let errors = 0

      for (let i = 0; i < excelRows.length; i += BATCH_SIZE) {
        const batch = excelRows.slice(i, i + BATCH_SIZE)
        const records = batch.map(row => {
          const unit = UNIT_MAP[row["Μ.Μ."] || row.Unit || ""] || "Item"
          const cats = buildCategoryArray(row)
          const desc = generateDescription(row)

          return {
            name: row.Name || "",
            sku: String(row.VendorCode || "").trim(),
            description: desc,
            provsku: row["Ονομασία Είδους"] || row.Description || "",
            category: JSON.stringify(cats),
            b2bsalesprice: row.StandardPrice || null,
            salesprice: row.DealerPrice || null,
            productDepth: row.Dimension1 || null,
            productWidth: row.Dimension2 || null,
            productHeight: row.Dimension3 || null,
            unit,
            vat: 24,
            usageType: "COMPLETE-PRODUCT",
          }
        })

        try {
          await postProduct({ resource: records })
          imported += batch.length
        } catch (err) {
          errors += batch.length
          addLog(`${t("Import batch")} ${Math.floor(i / BATCH_SIZE) + 1}: ${err.message}`, false)
        }
        setProgress({ done: imported + errors, total: excelRows.length, errors })
      }

      addLog(`${t("Imported")} ${imported} ${t("products")} (${errors} ${t("errors")})`)

      setResult({ deleted, imported, errors })
      onSnackbar?.({
        open: true,
        message: `${t("Done")}: ${deleted} ${t("deleted")}, ${imported} ${t("imported")}`,
        severity: errors > 0 ? "warning" : "success",
      })
    } catch (err) {
      addLog(`${t("Fatal error")}: ${err.message}`, false)
      onSnackbar?.({ open: true, message: err.message, severity: "error" })
    } finally {
      setRunning(false)
      setPhase("")
    }
  }

  const progressPercent = progress.total > 0
    ? Math.round((progress.done / progress.total) * 100)
    : 0

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Avatar sx={{ width: 40, height: 40, bgcolor: "#fef3c7", color: "#d97706" }}>
          <UploadFileIcon sx={{ fontSize: 20 }} />
        </Avatar>
        <Box>
          <Typography variant="subtitle1" fontWeight={600} color="#111827">
            {t("Catalog Update")}
          </Typography>
          <Typography variant="body2" color="#6b7280">
            {t("Delete all products & reimport from Excel catalog")}
          </Typography>
        </Box>
      </Stack>

      {/* Step 1: Upload */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: "none" }}
          onChange={handleFileUpload}
        />
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={loading || running}
          sx={{
            bgcolor: "#3b82f6",
            "&:hover": { bgcolor: "#2563eb" },
            borderRadius: 2,
            textTransform: "none",
          }}
        >
          {loading ? t("Parsing...") : t("1. Select Excel")}
        </Button>

        {fileName && (
          <Chip
            label={`${fileName} — ${excelRows.length} ${t("rows")}`}
            sx={{ bgcolor: "#e0f2fe", color: "#0369a1", fontWeight: 600 }}
          />
        )}
      </Stack>

      {/* Step 2: Delete & Reimport */}
      {excelRows.length > 0 && !result && (
        <Button
          variant="contained"
          startIcon={running ? <CircularProgress size={16} color="inherit" /> : <DeleteSweepIcon />}
          onClick={handleDeleteAndReimport}
          disabled={running}
          sx={{
            bgcolor: "#dc2626",
            "&:hover": { bgcolor: "#b91c1c" },
            borderRadius: 2,
            textTransform: "none",
            mb: 2,
          }}
        >
          {running
            ? `${phase === "deleting" ? t("Deleting") : t("Importing")}... ${progress.done}/${progress.total}`
            : `2. ${t("Delete All & Reimport")} (${excelRows.length} ${t("products")})`}
        </Button>
      )}

      {/* Progress bar */}
      {running && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="#64748b" sx={{ mb: 0.5, display: "block" }}>
            {phase === "deleting" ? t("Deleting existing products...") : t("Importing new products...")}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progressPercent}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: "#e2e8f0",
              "& .MuiLinearProgress-bar": {
                bgcolor: phase === "deleting" ? "#dc2626" : "#16a34a",
              },
            }}
          />
          <Typography variant="caption" color="#64748b" sx={{ mt: 0.5, display: "block" }}>
            {progress.done}/{progress.total} — {progressPercent}%
          </Typography>
        </Box>
      )}

      {/* Result */}
      {result && (
        <Alert
          icon={<CheckCircleIcon />}
          severity={result.errors > 0 ? "warning" : "success"}
          sx={{ mb: 2, borderRadius: 2 }}
        >
          <strong>{t("Done")}:</strong> {result.deleted} {t("deleted")}, {result.imported} {t("imported")}
          {result.errors > 0 && `, ${result.errors} ${t("errors")}`}
        </Alert>
      )}

      {/* Log */}
      {log.length > 0 && (
        <Box sx={{ maxHeight: 200, overflow: "auto", bgcolor: "#f8fafc", borderRadius: 1.5, p: 1.5, border: "1px solid #e2e8f0" }}>
          {log.map((entry, i) => (
            <Typography
              key={i}
              variant="caption"
              sx={{ display: "block", fontFamily: "monospace", color: entry.ok ? "#16a34a" : "#dc2626" }}
            >
              [{entry.time}] {entry.msg}
            </Typography>
          ))}
        </Box>
      )}

      {/* Empty state */}
      {excelRows.length === 0 && !loading && !running && !result && (
        <Box sx={{ textAlign: "center", py: 6, color: "#94a3b8" }}>
          <UploadFileIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
          <Typography variant="body2">
            {t("Select the kitchen catalog Excel to begin")}
          </Typography>
          <Typography variant="caption" color="#cbd5e1">
            ΚΩΔΙΚΟΙ ΤΙΜΟΚΑΤΑΛΟΓΟΥ ΚΟΥΖΙΝΑΣ v11.xlsx
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default CatalogUpdateTab
